import React from 'react'

interface IconProps {
  size?: number
  className?: string
}

/** Notepad with the inbox-tray indicator — used when Quick Notes has entries. */
export const QuickNotesFilledIcon: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path d="M15.66 3.66V14.66C15.66 15.22 15.22 15.66 14.66 15.66H1.67C1.11 15.66 0.67 15.22 0.67 14.66V3.66" stroke="currentColor" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.66 9.66H10.66V12.66H5.67V9.66H0.67" stroke="currentColor" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.67 7.66V0.66H13.66V7.66" stroke="currentColor" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.67 3.66H10.66" stroke="currentColor" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.67 6.66H10.66" stroke="currentColor" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/** Notepad with check-mark — used when Quick Notes has been emptied. */
export const QuickNotesEmptyIcon: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path d="M14.66 15.66H1.67C1.11 15.66 0.67 15.22 0.67 14.66V1.66C0.67 1.11 1.11 0.66 1.67 0.66H14.66C15.22 0.66 15.66 1.11 15.66 1.66V14.66C15.66 15.22 15.22 15.66 14.66 15.66Z" stroke="currentColor" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.66 9.66H10.66V12.66H5.67V9.66H0.67" stroke="currentColor" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.67 5.66L7.67 7.66L11.66 3.66" stroke="currentColor" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * Tab-open variant of QuickNotesFilledIcon — coloured violet to mirror the
 * "active tab" state of the sidebar. Fills + strokes use design tokens
 * (`--space-100/300/700`, equivalent to Tailwind violet-100/300/700) so the
 * icon adapts to the active theme.
 */
export const QuickNotesFilledOpenIcon: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path d="M1.17 1.16H15.16V9.16H10.66V12.66H5.67V9.16H1.17V1.16Z" fill="var(--space-100, #EDE9FE)" />
    <path d="M1.17 10.16H5.17V12.66H10.66V9.66H15.66V15.16H5.17H1.17V10.16Z" fill="var(--space-300, #C4B5FD)" />
    <path d="M15.66 3.66V14.66C15.66 15.22 15.22 15.66 14.66 15.66H1.67C1.11 15.66 0.67 15.22 0.67 14.66V3.66" stroke="var(--space-700, #6D28D9)" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.66 9.66H10.66V12.66H5.67V9.66H0.67" stroke="var(--space-700, #6D28D9)" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.67 7.66V0.66H13.66V7.66" stroke="var(--space-700, #6D28D9)" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.67 3.66H10.66" stroke="var(--space-700, #6D28D9)" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.67 6.66H10.66" stroke="var(--space-700, #6D28D9)" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/** Tab-open variant of QuickNotesEmptyIcon (violet, with check-mark). */
export const QuickNotesEmptyOpenIcon: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path d="M1.17 1.16H15.16V9.16H10.66V12.66H5.67V9.16H1.17V1.16Z" fill="var(--space-100, #EDE9FE)" />
    <path d="M1.17 10.16H5.17V12.66H10.66V9.66H15.66V15.16H5.17H1.17V10.16Z" fill="var(--space-300, #C4B5FD)" />
    <path d="M14.66 15.66H1.67C1.11 15.66 0.67 15.22 0.67 14.66V1.66C0.67 1.11 1.11 0.66 1.67 0.66H14.66C15.22 0.66 15.66 1.11 15.66 1.66V14.66C15.66 15.22 15.22 15.66 14.66 15.66Z" stroke="var(--space-700, #6D28D9)" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.66 9.66H10.66V12.66H5.67V9.66H0.67" stroke="var(--space-700, #6D28D9)" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.67 5.66L7.67 7.66L11.66 3.66" stroke="var(--space-700, #6D28D9)" strokeWidth="1.33" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)
